import { Card } from '@mdvip/shared/components/ui/card';
import { cn } from '@mdvip/shared/lib/utils';
import type { HTMLAttributes, ReactNode } from 'react';

/* Tone → static class strings (kept whole so Tailwind's scanner emits them).
   WASH is a soft diagonal gradient wash from the top-right corner; GLOW is the
   blurred corner orb layered on top of it. No inline styles. */
const WASH = {
  primary: 'bg-gradient-to-bl from-primary/10 via-transparent to-transparent',
  success: 'bg-gradient-to-bl from-success/10 via-transparent to-transparent',
  info: 'bg-gradient-to-bl from-info/10 via-transparent to-transparent',
  neutral: 'bg-gradient-to-bl from-foreground/[0.06] via-transparent to-transparent',
} as const;
const GLOW = {
  primary: 'bg-primary/10',
  success: 'bg-success/10',
  info: 'bg-info/10',
  neutral: 'bg-foreground/10',
} as const;

export interface CardDecorativeGradientProps extends HTMLAttributes<HTMLDivElement> {
  /** Accent tone for the gradient wash + corner glow. @default 'primary' */
  tone?: 'primary' | 'success' | 'info' | 'neutral';
  /** Extra decorative nodes rendered in the (behind-content) decorative layer, e.g. a watermark icon or dots. */
  decoration?: ReactNode;
  /** className for the decorative layer itself (e.g. 'hidden sm:block' to limit the decoration to desktop). */
  decorationClassName?: string;
}

/**
 * CardDecorativeGradient: a shadcn `Card` dressed with a soft, reusable decorative background: a
 * diagonal gradient wash plus a blurred corner glow, both tone-tinted and expressed purely with
 * Tailwind classes (no inline styles). Children render above the decoration (z-10). Pass
 * `decoration` for extra behind-content motif (a watermark icon, dots…) and `decorationClassName`
 * to scope the whole decorative layer (e.g. 'hidden sm:block' for desktop-only). Generic, so use it
 * for any card that wants a branded gradient backdrop, not just AutoPay. Composes the DS `Card`.
 */
export function CardDecorativeGradient({
  tone = 'primary',
  decoration,
  decorationClassName,
  children,
  className,
  ...props
}: CardDecorativeGradientProps) {
  const wash = WASH[tone] ?? WASH.primary;
  const glow = GLOW[tone] ?? GLOW.primary;
  return (
    <Card data-slot="card-decorative-gradient" className={cn('relative overflow-hidden', className)} {...props}>
      <div aria-hidden className={cn('pointer-events-none absolute inset-0 select-none', decorationClassName)}>
        <div className={cn('absolute inset-0', wash)} />
        <div className={cn('absolute -right-12 -top-14 h-52 w-52 rounded-full blur-2xl', glow)} />
        {decoration}
      </div>
      <div className="relative z-10 flex h-full flex-col">{children}</div>
    </Card>
  );
}
